import type { ConversationStatus } from "~/ai-chat/model/conversation";
import { cn } from "~/utils/cn";

export type ConversationDotState = "read" | "unread" | "processing" | "paused" | "error";

export const CONVERSATION_DOT_LABEL: Record<ConversationDotState, string | null> = {
  read: null,
  unread: "Unread",
  processing: "Processing",
  paused: "Awaiting approval",
  error: "Error",
};

// `isRead`: the user has seen the conversation since its last update (see the
// conversation read store); a finished turn they have not seen yet is "unread".
export function conversationDotState(status: ConversationStatus | null, isRead: boolean): ConversationDotState {
  if (status === "running") return "processing";
  // Waiting on the user, not the model: shown like processing, whether or not it has been read.
  if (status === "paused") return "paused";
  if (status === "error") return "error";
  if (status === "completed" && !isRead) return "unread";
  return "read";
}

export function ConversationStatusDot({ state, className }: { state: ConversationDotState; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block size-2.5 shrink-0 rounded-full border-[1.33px]", STATE_CLASS[state], className)}
    />
  );
}

const STATE_CLASS: Record<ConversationDotState, string> = {
  read: "border-muted-foreground bg-transparent",
  unread: "border-chat-status-unread-ring bg-chat-status-unread",
  processing: "border-chat-status-processing-ring bg-chat-status-processing",
  paused: "border-chat-status-processing-ring bg-chat-status-processing",
  error: "border-chat-status-error-ring bg-chat-status-error",
};
